import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import CleaningZone, { isTeachAndRepeatZone } from "../../../../interfaces/robot/rooms/cleaning-zone";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import { selectIsAdvancedModeEnabled } from "../../../../store/app-configurations/app-configurations-selectors";
import { selectSelectedRoom } from "../../../../store/room/room-selectors";
import {
  CleaningConfiguration,
  CleaningConfigurationLevel,
} from "../../../../interfaces/robot/cleaning-configurations/global-cleaning-configuration";
import {
  selectGlobalCleaningConfiguration,
  selectGlobalMapRulesConfiguration,
  selectParamListRestState,
} from "../../../../store/robot-configurations/robot-configurations-selectors";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";
import CustomAlert from "../../../../components/Feedback/CustomAlert";
import CustomLoader from "../../../../components/Feedback/CustomLoader";
import EnvironmentCleaningConfiguration from "../../../Configuration/CleaningConfiguration/global/EnvironmentCleaningConfiguration";
import { MapRulesConfiguration } from "../../../../interfaces/robot/cleaning-configurations/map-rules-configuration";

const CleaningSettingsForm: React.FC<{ zone: CleaningZone }> = ({ zone }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { getParamList, patchCleaningZone } = useSharedActions();

  const isAdvancedModeEnabled = useSharedSelector((state) => selectIsAdvancedModeEnabled(state));
  const room = useSharedSelector((state) => selectSelectedRoom(state));
  const globalCleaningConfiguration = useSharedSelector((state) => selectGlobalCleaningConfiguration(state));
  const globalMapRulesConfiguration = useSharedSelector((state) => selectGlobalMapRulesConfiguration(state));
  const paramListRest = useSharedSelector((state) => selectParamListRestState(state));

  useEffect(() => {
    if (paramListRest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getParamList());
    }
  }, [dispatch, getParamList, paramListRest.getStatus]);

  const updateCleaningConfigurationHandler = (
    payload: Partial<CleaningConfiguration> | Partial<MapRulesConfiguration>,
  ) => {
    dispatch(
      patchCleaningZone({
        id: zone.id,
        data: { cleaningZone: { cleaningConfiguration: { ...zone.cleaningConfiguration, ...payload } } },
      }),
    );
  };

  if ([HttpFetchStatus.Idle, HttpFetchStatus.Loading].includes(paramListRest.getStatus))
    return <CustomLoader loadingMessage={t("loading-global-cleaning-configuration")} />;
  if (globalCleaningConfiguration == null)
    return <CustomAlert header={t("error-loading-global-cleaning-configuration")} />;

  return (
    <EnvironmentCleaningConfiguration
      hideDivider
      zoneCleaningConfiguration={zone.cleaningConfiguration}
      configurationLevel={CleaningConfigurationLevel.Zone}
      onConfigurationChange={updateCleaningConfigurationHandler}
      globalCleaningConfiguration={globalCleaningConfiguration}
      globalMapRulesConfiguration={globalMapRulesConfiguration}
      roomCleaningConfiguration={room?.cleaningConfiguration}
      roomHasMapFiles={room?.hasPosegraphAndDataFiles}
      allowSpatialPropertyConfig={isAdvancedModeEnabled && !isTeachAndRepeatZone(zone)}
    />
  );
};

export default CleaningSettingsForm;
